import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { Content } from 'src/app/models/content';
import { ACTIONS, ENVIRONMENTS } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { ENVIRONMENT } from 'src/environments/environment';
import { LoadDrbIds } from '../../drbs/redux';
import { ContentState, DeleteContent, LoadContent, ReloadContent } from '../redux';
import { EditContentComponent } from './edit-content/edit-content.component';
import { ViewContentComponent } from './view-content/view-content.component';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
  selector: 'app-content',
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.css'],
  providers: [DecimalPipe],
  imports: [SharedModule],
  standalone: true
})
export class ContentComponent implements OnInit, OnDestroy {
  public content$: Observable<any[]> = inject(Store).select(ContentState.content);
  private unsubscribe: Subject<void> = new Subject();

  public tableData;
  public showActions = [];

  constructor(
    private store: Store,
    private dialogService: NbDialogService,
    private restrictionService: OktaRestriction
  ) {
    if (!this.store.selectSnapshot(ContentState.content)) {
      this.store.dispatch(LoadContent);
    }
  }

  public ngOnInit() {
    this.content$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
    if (this.restrictionService.manager_permission) {
      this.showActions = [ACTIONS.CREATE, ACTIONS.EDIT, ACTIONS.CLONE, ACTIONS.DELETE];
    }
    if (ENVIRONMENT.name !== ENVIRONMENTS.TKPROD) {
      this.store.dispatch(new LoadDrbIds());
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadContent);
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: new Content()
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const content: Content = $event.data;
      this.dialogService.open(ViewContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          content
        }
      });
    }
    if ($event.action === ACTIONS.EDIT) {
      const content: Content = $event.data;
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: Content.copy(content) as Content
        }
      });
    }
    if ($event.action === ACTIONS.CLONE) {
      const content: Content = $event.data;
      this.dialogService.open(EditContentComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          content: Content.clone(content)
        }
      });
    }
    if ($event.action === ACTIONS.DELETE) {
      const content: Content = $event.data;
      this.dialogService
        .open(NgxGenericModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            title: $localize`Are you sure you want to delete Content`,
            message: content.content_id + ' - ' + content.headline_text
          }
        })
        .onClose.subscribe((data) => {
          if (data === true) {
            const contentToDelete = Content.copy(content);
            contentToDelete.deleted = true;
            this.store.dispatch(new DeleteContent(contentToDelete));
          }
        });
    }
  }
}
